import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle, loginWithFacebook, error, setError } = useAuth();
  const navigate = useNavigate();

  // Handle email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      return setError('Por favor llena todos los campos');
    }
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      // Show user-friendly error messages
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Correo electrónico o contraseña incorrecta');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Demasiados intentos fallidos. Por favor intenta más tarde');
      } else {
        setError('Error al iniciar sesión. Por favor intenta de nuevo');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google login
  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/');
    } catch (error) {
      console.error('Google login error:', error);
      setError('Error al iniciar sesión con Google. Por favor intenta de nuevo');
    } finally {
      setLoading(false);
    }
  };

  // Handle Facebook login
  const handleFacebookLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithFacebook();
      navigate('/');
    } catch (error) {
      console.error('Facebook login error:', error);
      setError('Error al iniciar sesión con Facebook. Por favor intenta de nuevo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography component="h1" variant="h4" sx={{ mb: 3 }}>
            Iniciar Sesión
          </Typography>
          
          {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  '&:hover fieldset': {
                    borderColor: '#F2C641',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F2C641',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#F2C641',
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  '&:hover fieldset': {
                    borderColor: '#F2C641',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#F2C641',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#F2C641',
                },
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2, py: 1.5, backgroundColor: '#F2C641', '&:hover': { backgroundColor: '#d9b13b' } }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
            </Button>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Link to="/forgot-password" style={{ textDecoration: 'none', color: '#1976d2' }}>
                <Typography variant="body2">¿Olvidaste tu contraseña?</Typography>
              </Link>
            </Box>
            
            <Divider sx={{ my: 2 }}>o</Divider>
            
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{ 
                py: 1.5, 
                mb: 2,
                backgroundColor: '#f2f7ff',
                color: '#4285F4',
                fontWeight: 500,
                borderColor: '#4285F4',
                borderWidth: '1px',
                '&:hover': { 
                  backgroundColor: '#e8f0fe',
                  borderColor: '#4285F4',
                  boxShadow: '0 1px 3px rgba(66,133,244,0.3)'
                },
                position: 'relative',
                boxShadow: '0px 1px 2px rgba(0,0,0,0.08)',
                '& .MuiButton-startIcon': {
                  position: 'absolute',
                  left: '16px',
                  '& svg': {
                    color: '#4285F4',
                    fontSize: '1.2rem'
                  }
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Iniciar Sesión con Google
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FacebookIcon />}
              onClick={handleFacebookLogin}
              disabled={loading}
              sx={{ 
                py: 1.5, 
                backgroundColor: '#1877F2',
                color: 'white',
                '&:hover': { 
                  backgroundColor: '#0d65d9',
                  borderColor: '#0d65d9'
                },
                borderColor: '#1877F2'
              }}
            >
              Iniciar Sesión con Facebook
            </Button>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2">
              ¿No tienes una cuenta?{' '}
              <Link to="/register" style={{ textDecoration: 'none', color: '#1976d2' }}>
                Regístrate
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 